import { httpClient } from './httpClient';
import { tokenStorage } from '../utils/tokenStorage';

export type SectionType = 'static' | 'variable' | 'placeholder';
export type CredentialSource = 'document' | 'profile';

export interface TemplateSection {
  id: string;
  title: string;
  originalTitle?: string;  // original DOCX heading (for content injection matching)
  placeholder?: string;
  required: boolean;
  aiGenerated?: boolean;
  sectionType?: SectionType;
  credentialSource?: CredentialSource; // for credential sections: 'document' (static from DOCX) or 'profile' (AI-generated from expert profile)
  order?: number;
  headingLevel?: number;
}

export interface ClassifiedSection {
  id: string;
  title: string;          // generalised template title
  originalTitle: string;  // original heading from the document
  bodyPreview: string;
  paragraphIndex: number;
  headingLevel: number;
  sectionType: SectionType;
  originalSectionType?: SectionType; // LLM's original classification — never changes after initial load
  classificationReason: string;
  guidance: string | null; // generation guidance for variable/placeholder sections
  depth: number;           // 1 = top-level section, 2 = sub-section
  isCredentialSection: boolean; // true if section is about expert credentials/qualifications
  credentialSource?: CredentialSource; // user choice: 'document' or 'profile'
}

export interface Template {
  id: string;
  name: string;
  category: string;
  description?: string;
  isPublic: boolean;
  createdBy: string;
  sections: string; // JSON string of TemplateSection[]
  hasStyleReference?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTemplateRequest {
  name: string;
  category: string;
  description?: string;
  isPublic: boolean;
  sections: string; // JSON string of TemplateSection[]
}

export interface UpdateTemplateRequest extends CreateTemplateRequest {
  id: string;
}

export const templateApi = {
  // Get all templates (user's own + public)
  getTemplates: async (): Promise<Template[]> => {
    return await httpClient.get<Template[]>('/templates');
  },

  // Get public templates only
  getPublicTemplates: async (): Promise<Template[]> => {
    return await httpClient.get<Template[]>('/templates/public');
  },

  // Get template by ID
  getTemplate: async (id: string): Promise<Template> => {
    return await httpClient.get<Template>(`/templates/${id}`);
  },

  // Create template
  createTemplate: async (request: CreateTemplateRequest): Promise<Template> => {
    return await httpClient.post<Template>('/templates', request);
  },

  // Update template
  updateTemplate: async (id: string, request: UpdateTemplateRequest): Promise<Template> => {
    return await httpClient.put<Template>(`/templates/${id}`, request);
  },

  // Delete template
  deleteTemplate: async (id: string): Promise<void> => {
    await httpClient.delete(`/templates/${id}`);
  },

  // --- DOCX template endpoints ---

  // Extract and classify sections from a DOCX file
  extractSectionsFromDocx: async (file: File): Promise<ClassifiedSection[]> => {
    const formData = new FormData();
    formData.append('file', file);

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
    const token = tokenStorage.getToken();

    const response = await fetch(`${API_BASE_URL}/templates/extract-sections`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.message || `Failed to extract sections (${response.status})`);
    }

    const json = await response.json();
    return json.data ?? json;
  },

  // Create a template from an uploaded DOCX file
  createFromDocx: async (
    file: File,
    name: string,
    category: string,
    sections: string,
    description?: string,
  ): Promise<Template> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('category', category);
    formData.append('sections', sections);
    if (description) formData.append('description', description);

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
    const token = tokenStorage.getToken();

    const response = await fetch(`${API_BASE_URL}/templates/from-docx`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.message || `Failed to create template (${response.status})`);
    }

    const json = await response.json();
    return json.data ?? json;
  },

  // Download the original DOCX style reference
  downloadStyleReference: async (templateId: string): Promise<Blob> => {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
    const token = tokenStorage.getToken();

    const response = await fetch(`${API_BASE_URL}/templates/${templateId}/style-reference`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new Error(`Failed to download style reference (${response.status})`);
    }

    return response.blob();
  },

  // Remove the DOCX style reference from a template
  deleteStyleReference: async (templateId: string): Promise<void> => {
    await httpClient.delete(`/templates/${templateId}/style-reference`);
  },

  // Parse sections from JSON string
  parseSections: (sectionsJson: string): TemplateSection[] => {
    try {
      const parsed = JSON.parse(sectionsJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Failed to parse template sections:', error);
      return [];
    }
  },

  // Stringify sections to JSON
  stringifySections: (sections: TemplateSection[]): string => {
    return JSON.stringify(sections);
  }
};
